import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api.service';
import { Provider, ProviderSearchFilters } from '../types/provider.types';
import './ProviderSearch.css';

type SortField = 'npi' | 'name' | 'specialty' | 'locations' | 'osFlag' | 'isReferable';
type SortDirection = 'asc' | 'desc';

const ProviderSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL params
  const [filters, setFilters] = useState<ProviderSearchFilters>({
    keyword: searchParams.get('keyword') || '',
    npi: searchParams.get('npi') || '',
    specialty: '',
    location: '',
    acceptingNewPatients: undefined,
    gender: '',
    language: ''
  });
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const pageSize = 25;

  // Load search results on mount if there are search params
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    const npi = searchParams.get('npi');
    if (keyword || npi) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 [ProviderSearch] handleSearch called with page:', page);
      console.log('🔍 [ProviderSearch] filters:', filters);
      console.log('🔍 [ProviderSearch] pageSize:', pageSize);

      // Update URL with search params
      const params = new URLSearchParams();
      if (filters.keyword) params.set('keyword', filters.keyword);
      if (filters.npi) params.set('npi', filters.npi);
      setSearchParams(params);

      // Fetch specific page from API
      console.log('🔍 [ProviderSearch] Calling apiService.searchProviders with perPage:', pageSize, 'pageNumber:', page);
      const results = await apiService.searchProviders(filters, pageSize, page);
      console.log('🔍 [ProviderSearch] API Response:', {
        totalProviders: results.totalProviders,
        providerResultsLength: results.providerResults?.length,
        providersLength: results.providers?.length
      });

      // Extract provider objects from providerResults array
      const providerList = results.providerResults?.map((item: any) => item.provider) || [];
      console.log('🔍 [ProviderSearch] Extracted provider list length:', providerList.length);

      setProviders(providerList);
      setTotalResults(results.totalProviders || providerList.length);
      setCurrentPage(page);

      console.log('🔍 [ProviderSearch] State updated - currentPage:', page, 'totalResults:', results.totalProviders || providerList.length);
    } catch (err: any) {
      console.error('❌ [ProviderSearch] Search error:', err);
      const errorMessage = err.message || 'Failed to search providers';

      // If it's a timeout on a later page, show a more helpful message
      if (errorMessage.includes('timeout') && page > 1) {
        setError(`Page ${page} timed out. The API may not have data for this page. Try going back to page ${page - 1}.`);
      } else {
        setError(errorMessage);
      }

      // Don't clear providers on pagination errors, keep showing previous page
      if (page === 1) {
        setProviders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof ProviderSearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      keyword: '',
      npi: '',
      specialty: '',
      location: '',
      acceptingNewPatients: undefined,
      gender: '',
      language: ''
    });
    setProviders([]);
    setError(null);
  };

  const handleSort = (field: SortField) => {
    // Note: Sorting is currently disabled with server-side pagination
    // To enable, would need to pass sort parameters to API
  };

  // Pagination - providers come pre-paginated from API
  const totalPages = Math.ceil(totalResults / pageSize);

  return (
    <div className="provider-search">
      <div className="search-header">
        <h2>Search Providers</h2>
        <p className="search-description">
          Search for existing providers from our Symplr Directory to inspect their readiness to display appointment availability.
        </p>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Name</label>
            <input
              type="text"
              placeholder="Provider name"
              value={filters.keyword}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="filter-group">
            <label>NPI</label>
            <input
              type="text"
              placeholder="NPI number"
              value={filters.npi}
              onChange={(e) => handleFilterChange('npi', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={() => handleSearch(1)} disabled={loading} className="btn btn-primary">
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button onClick={handleClearFilters} className="btn btn-secondary">
            Clear Filters
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {providers.length > 0 && (
        <div className="results-section" style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '8px'
            }}>
              <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          )}
          <div className="results-header">
            <p className="results-count">
              Found {totalResults} provider{totalResults !== 1 ? 's' : ''}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </p>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('npi')}>
                    NPI {sortField === 'npi' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('specialty')}>
                    Specialty {sortField === 'specialty' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('locations')}>
                    Locations {sortField === 'locations' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('osFlag')}>
                    Open Sch Enabled {sortField === 'osFlag' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('isReferable')}>
                    Is Referable {sortField === 'isReferable' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider, index) => (
                  <tr key={provider.npi || index}>
                    <td>{provider.npi || 'N/A'}</td>
                    <td>
                      <div className="provider-name">
                        <div className="provider-thumbnail-wrapper">
                          {provider.photo ? (
                            <img
                              src={provider.photo}
                              alt={provider.displayName || provider.fullName || 'Provider'}
                              className="provider-thumbnail"
                            />
                          ) : (
                            <div className="provider-thumbnail-placeholder">
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div>
                          <strong>{provider.displayName || provider.fullName || 'Unknown'}</strong>
                          {provider.degrees && provider.degrees.length > 0 && (
                            <span className="degrees">
                              {provider.degrees.map(d => d.code).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {provider.specialties && provider.specialties.length > 0
                        ? provider.specialties.map(s => s.Specialty || s.specialty || s.Name || s.name).filter(Boolean).join(', ')
                        : 'N/A'}
                    </td>
                    <td>{provider.locationCount || provider.locations?.length || 0}</td>
                    <td>
                      {provider.isOpenSchedulingAvailable ? (
                        <span className="status-yes">Yes</span>
                      ) : (
                        'No'
                      )}
                    </td>
                    <td>
                      {provider.isReferable ? (
                        <span className="status-yes">Yes</span>
                      ) : (
                        'No'
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => provider.npi && navigate(`/provider/${provider.npi}`)}
                        className="btn btn-small"
                        disabled={!provider.npi}
                      >
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handleSearch(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || loading}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handleSearch(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || loading}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && providers.length === 0 && !error && (
        <div className="no-results">
          <p>No providers found. Try adjusting your search filters.</p>
        </div>
      )}
    </div>
  );
};

export default ProviderSearch;
